import React, { useEffect, useState, useRef } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';

import { api } from '../../../services/api';

import { useForm } from '../../../utils/custom-hooks';
import { STRATEGY_GROUP_TYPES } from '../../../utils/constants';

import BreadcrumbNav from '../../ui/breadcrumb';
import DropdownSelect from '../../ui/dropdown';
import FormNav from '../../ui/form-elements/form-page/form-nav';
import FormSection from '../../ui/form-elements/form-page/form-section';
import InputWrapper from '../../ui/form-elements/form-page/input-wrapper';
import TextInput from '../../ui/form-elements/text-input/text-input';
import TextArea from '../../ui/form-elements/text-area/text-area';
import Button from '../../ui/button';

import { dateRange, max, required } from '../../ui/form-elements/validators';
import NumberInput from '../../ui/form-elements/text-input/number-input';
import DateRangePicker from '../../ui/form-elements/date-picker/date-picker';

import Modal from '../../base/modals/base-modal';

import './campaign-creation.scss';

const CampaignCreation = props => {
  const { merchantId, strategyGroupId } = useParams();
  const history = useHistory();

  const [defaultValues, setDefaultValues] = useState(null);
  const [campaignType, setCampaignType] = useState(null);
  const [formError, setFormError] = useState(null);
  const [isDiscardModalOpen, setDiscardModalOpen] = useState(false);
  const currentNav = useRef(null);

  const getEndpoint = `/api/v0/merchants/${merchantId}/strategy_groups/${strategyGroupId}/strategy_group_by_id/`;
  const createEndpoint = `/api/v0/merchants/${merchantId}/create_strategy_group/`;
  const updateEndpoint = `/api/v0/merchants/${merchantId}/strategy_groups/${strategyGroupId}/update_strategy_group/`;

  //  Form Handling
  const validators = {
    strategy_group_name: [required()],
    // strategy_group_budget_type: [required()],
    strategy_group_type: [required()],
    strategy_group_datetime_start: [required(), dateRange('strategy_group_datetime_end')],
    strategy_group_budget_amount: [required(), max(9999999.99)],
  };

  const onSubmit = () => {
    unblock();
    const payload = formatPayload(values);
    const apiCall = strategyGroupId
      ? Promise.resolve(api.put(updateEndpoint, { strategy_group: payload }))
      : Promise.resolve(api.post(createEndpoint, { strategy_group: payload }));

    apiCall
      .then(resp => {
        const strategyGroupId = resp.data.data[0].strategy_group_id;
        strategyGroupId
          ? history.push(`/v2/merchant/${merchantId}/campaign-center/budgets`)
          : history.push(
              `/v2/merchant/${merchantId}/campaign-center/campaigns?strategy_group_ids=${strategyGroupId}`,
            );
      })
      .catch(err => {
        setFormError(err.message);
      });
  };

  const { values, setValues, errors, handleChange, handleSubmit, isDirty } = useForm(
    onSubmit,
    validators,
  );

  //  Navigation intercept for unsaved form
  const unblock = history.block(nav => {
    currentNav.current = nav;
    if (isDirty) {
      setDiscardModalOpen(true);
      return false;
    }
    return true;
  });

  //  Initial Form State Default Values
  //  TODO: Find cleaner way to do this, e.g. schema class, useForm argument?
  useEffect(() => {
    if (strategyGroupId) {
      api.get(getEndpoint).then(resp => {
        setValues(resp.data.data[0]);
        setDefaultValues(resp.data.data[0]);
      });
    } else {
      const defaultCampaignValues = {
        strategy_group_name: undefined,
        strategy_group_description: undefined,
        strategy_group_type: 2,
        strategy_group_budget_type: undefined,
        strategy_group_budget_amount: undefined,
        strategy_group_datetime_start: undefined,
        strategy_group_datetime_end: undefined,
      };

      setValues(defaultCampaignValues);
      setDefaultValues(defaultCampaignValues);
    }
  }, []);

  //  TODO: Make into utility class
  const formatPayload = payload => {
    const formattedPayload = Object.assign(values, payload);

    const payloadFields = [
      'strategy_group_name',
      'strategy_group_description',
      'strategy_group_type',
      // 'strategy_group_budget_type',
      'strategy_group_is_enabled',
      'strategy_group_budget_amount',
      'strategy_group_datetime_start',
      'strategy_group_datetime_end',
    ];

    Object.keys(formattedPayload).forEach(field => {
      if (!payloadFields.includes(field)) {
        delete formattedPayload[field];
      }
    });

    formattedPayload.strategy_group_budget_amount = parseFloat(
      payload.strategy_group_budget_amount,
    );
    // formattedPayload.strategy_group_budget_type = parseInt(payload.strategy_group_budget_type);
    formattedPayload.strategy_group_type = STRATEGY_GROUP_TYPES[payload.strategy_group_type];
    formattedPayload.strategy_group_is_enabled = true;
    if (!strategyGroupId) {
      formattedPayload.merch_id = parseInt(merchantId);
    }

    if (
      !formattedPayload.strategy_group_description ||
      formattedPayload.strategy_group_description.trim().length === 0
    ) {
      formattedPayload.strategy_group_description = null;
    }

    return formattedPayload;
  };

  const formatStrategyGroupType = groupType => {
    let typeLabel = groupType;
    // Updating via dropdown uses enum identifiers rather than string values, so get values from enum in that case
    if (parseFloat(groupType)) {
      typeLabel = STRATEGY_GROUP_TYPES[parseFloat(groupType)];
    }
    return typeLabel.split('_').join(' ');
  };

  return (
    <div className="campaign-creation">
      <BreadcrumbNav
        crumbTrail={[
          { label: 'All Budgets', href: `/v2/merchant/${merchantId}/campaign-center/budgets` },
          {
            label:
              strategyGroupId && defaultValues
                ? defaultValues.strategy_group_name
                : 'Create Budget',
          },
        ]}
      />
      <h2>{strategyGroupId ? 'Edit' : 'Create'} Budget</h2>
      <FormNav steps={['Set Up Budget']} currentStep={1} />
      <form onSubmit={handleSubmit}>
        <FormSection label="Budget Name">
          <InputWrapper name="strategy_group_name" label="Name" required={true}>
            <TextInput
              name="strategy_group_name"
              value={values.strategy_group_name || ''}
              onChange={handleChange}
              errors={errors.strategy_group_name}
            />
          </InputWrapper>
          <InputWrapper name="strategy_group_description" label="Description">
            <TextArea
              name="strategy_group_description"
              value={values.strategy_group_description || ''}
              onChange={handleChange}
              errors={errors.strategy_group_description}
            />
          </InputWrapper>
          <InputWrapper name="strategy_group_type" label="Budget Type" required={true} width={50}>
            <DropdownSelect
              selected={values.strategy_group_type || null}
              onChange={handleChange}
              onSelect={setCampaignType}
              styleType="bordered"
              formElement={true}
              name="strategy_group_type"
              matchToggleWidth={true}
              label={
                values.strategy_group_type
                  ? formatStrategyGroupType(values.strategy_group_type)
                  : 'Select Type'
              }
              menuAlign="left"
              errors={errors.strategy_group_type}
              items={[
                { label: 'Vendor Funded', value: '1' },
                { label: 'Marketplace', value: '2' },
                { label: 'Direct Deal', value: '3' },
              ]}
            />
          </InputWrapper>
        </FormSection>
        <FormSection
          label="Schedule & Budget"
          description={
            <div>
              The budget you set for a campaign is your total budget pool. This is the total budget
              you'll be allocating to strategies within this campaign.
            </div>
          }
        >
          <InputWrapper
            name="strategy_budget_amount"
            label="Budget Amount"
            required={true}
            width={25}
          >
            <NumberInput
              name="strategy_group_budget_amount"
              value={values.strategy_group_budget_amount || ''}
              onChange={handleChange}
              subLabel={'US Dollars'}
              errors={errors.strategy_group_budget_amount}
              disabled={values.strategy_group_is_default}
            />
          </InputWrapper>
          <InputWrapper label="Date" required={true} width={50}>
            <DateRangePicker
              datetimeStart={values.strategy_group_datetime_start || null}
              datetimeEnd={values.strategy_group_datetime_end || null}
              onChange={handleChange}
              startDateField="strategy_group_datetime_start"
              endDateField="strategy_group_datetime_end"
              endDateOptional={true}
              errors={{
                strategy_group_datetime_start: errors.strategy_group_datetime_start,
                strategy_group_datetime_end: errors.strategy_group_datetime_end,
              }}
            />
          </InputWrapper>
        </FormSection>
        <FormSection>
          <div className="form-buttons">
            <div className="left" />
            <div className="right">
              <span className="form-error">{formError}</span>
              <Button
                onClick={e => {
                  e.preventDefault();
                  history.push(`/v2/merchant/${merchantId}/campaign-center/budgets/`);
                }}
                kind="text"
                label="Cancel"
                size="large"
              />
              <Button
                submit
                kind="primary"
                label={`${strategyGroupId ? 'Edit' : 'Create'} Budget`}
                size="large"
              />
            </div>
          </div>
        </FormSection>
      </form>
      <Modal
        isShowing={isDiscardModalOpen}
        options={{
          identifier: 'campaign-creation',
          displayMsg: 'Discard changes?',
          displayTxt:
            "You haven't saved changes to your campaign. Are you sure you want to leave the page?",
          save: {
            txt: 'Discard Changes',
            callback: () => {
              unblock();
              currentNav.current && history.push(currentNav.current.pathname);
            },
          },
          cancel: {
            txt: 'Cancel',
            callback: () => setDiscardModalOpen(false),
          },
        }}
      />
    </div>
  );
};

export default CampaignCreation;
